import { PropertyType } from '@app/models/enums'
import { bindFunctions } from '@app/utils/class'
import { Loader } from '@googlemaps/js-api-loader'
import { List } from 'immutable'
import * as React from 'react'
import DocumentTitle from 'react-document-title'
import { IMapMarkerData } from '../modules/mapView'

type MapComponentState = {
  filter: List<PropertyType> | string
  totalItems: number
  items: List<IMapMarkerData>
  isLoading: boolean
}

export default class MapComponent extends React.PureComponent<MapComponentState> {
  constructor(props: MapComponentState) {
    super(props)

    bindFunctions(this)
    this.markers = []
  }

  private map: google.maps.Map
  private markers: { marker: google.maps.Marker; item: IMapMarkerData }[]
  private search: google.maps.places.Autocomplete

  public componentDidMount() {
    this._createMap(this.props.items)
  }

  public componentDidUpdate() {
    this.markers.forEach((x) => x.marker.setMap(null))
    this.markers = []
    this._addMarkers(this.props.items)
    this._resetMarkers()
  }

  public render() {
    return this.props.isLoading ? (
      <div>Loading</div>
    ) : (
      <DocumentTitle title='Map'>
        <div
          className='mt-4 w-100 h-75 rounded border border-2 border-secondary'
          id='mapdiv'
        />
      </DocumentTitle>
    )
  }

  private async _createMap(items: List<IMapMarkerData>) {
    const loader = new Loader({
      apiKey: 'AIzaSyCWte3eHItNFTkYk0ZtpTxx_kO1Ph7kKj0',
      libraries: ['drawing', 'places'],
    })
    const mapOptions = {
      center: {
        lat: 35.816823588392126,
        lng: -90.69156298371206,
      },
      zoom: 16,
    }
    const google = await loader.load()
    this.map = new google.maps.Map(
      document.getElementById('mapdiv')!,
      mapOptions,
    )
    this._addMarkers(items)
    this.search = new google.maps.places.Autocomplete(
      document.getElementById('input') as HTMLInputElement,
      {
        componentRestrictions: { country: ['us'] },
        fields: ['address_components', 'geometry'],
        types: ['address'],
      },
    )
    this.search.addListener('place_changed', () => {
      this._searchLocation()
      this._updateMarkers(this.map.getBounds()!)
    })

    // Needs this to render markers after map loads, otherwise waits for zoom / drag
    // https://stackoverflow.com/questions/832692/how-can-i-check-whether-google-maps-is-fully-loaded
    this.map.addListener('idle', () => {
      this._updateMarkers(this.map.getBounds()!)
    })
    this.map.addListener('zoom_changed', () => {
      this._updateMarkers(this.map.getBounds()!)
    })
    this.map.addListener('dragend', () => {
      this._updateMarkers(this.map.getBounds()!)
    })
    this.map.addListener('reloadMarkers', () => {
      this._updateMarkers(this.map.getBounds()!)
    })
  }

  private _showMarker(
    filter: string | List<PropertyType>,
    marker: { marker: google.maps.Marker; item: IMapMarkerData },
  ) {
    if (typeof filter == 'string') {
      return marker.item.status === this.props.filter || filter === 'All'
    }
    if (filter.count() === 0) {
      return true
    }
    return marker.item.propertyTypes
      ? marker.item.propertyTypes.some((x) => filter.includes(x!))
      : false
  }

  private _updateMarkers(bounds: google.maps.LatLngBounds) {
    this.markers.map((marker) => {
      const position = marker.marker.getPosition()
      if (
        bounds.contains(position!) &&
        (this.props.filter === 'All' ||
          this._showMarker(this.props.filter, marker))
      ) {
        marker.marker.setMap(this.map)
      } else {
        marker.marker.setMap(null)
      }
    })
  }

  private _addMarkers(items: List<IMapMarkerData>) {
    items.map((item) => {
      if (
        item &&
        item.gps &&
        item.gps.latitude !== 0 &&
        item.gps.longitude !== 0
      ) {
        this._addMarker(
          {
            lat: item.gps.latitude,
            lng: item.gps.longitude,
          },
          item,
        )
      }
    })
  }

  private _addMarker(
    { lat, lng }: { lat: number; lng: number },
    item: IMapMarkerData,
  ) {
    const marker = new google.maps.Marker({
      position: {
        lat,
        lng,
      },
      map: null,
      icon: '/images/hb-marker.png',
    })
    marker.addListener('click', () =>
      window.open(
        `/${
          item.type === 'property' ? 'properties/property' : 'brokerage/listing'
        }/${item.id}`,
      ),
    )
    this.markers.push({ marker, item })
  }

  private _resetMarkers() {
    google.maps.event.trigger(this.map, 'reloadMarkers')
  }

  private _searchLocation() {
    const place = this.search.getPlace()
    this.map.panTo(place.geometry!.location!)
    this.map.setZoom(16)
  }
}
